import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Event, NavigationEnd, Router } from '@angular/router';
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import { Auth } from 'aws-amplify';
import { filter, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FeedbackModalComponent } from './feedback-modal/feedback-modal.component';
import { CognitoService } from './services/cognito.service';
import { MenuProviderService } from './services/menu-provider.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  host: { class: 'mat-theme' }
})
export class AppComponent implements OnInit, OnDestroy {
  isAdmin = false;
  isOpen = false;
  isAuthenticated = false;
  private authSubscription: Subscription | null = null;
  userEmail = '';
  public enableReviewers = environment.features.enableReviewers;
  public enableNewAdvisor = environment.features.enableNewAdvisor;
  // Used for highlighting the current route in the sidenav
  currentRoute = this.router.url;

  constructor(
    public authenticator: AuthenticatorService,
    public cognitoService: CognitoService,
    private router: Router,
    private menuProvider: MenuProviderService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    document.documentElement.style.setProperty('--sidebar-width', this.isOpen ? '240px' : '64px');
    // Subscribe to authentication status
    this.authSubscription = this.cognitoService.isAuthenticated().subscribe(async isAuth => {
      this.isAuthenticated = isAuth;
      if (isAuth) {
        this.isAdmin = await this.cognitoService.isAdmin();
        this.menuProvider.setFlags(this.isAdmin, this.enableNewAdvisor, this.enableReviewers);
        this.getUserEmail();
      } else {
        this.isAdmin = false;
      }
    });

    this.router.events
      .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.url;
      });
  }

  ngOnDestroy() {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  get menuItems() {
    return this.menuProvider.getMenuItems();
  }

  // toggle sidenav and alert service for other components that may need to know
  toggleSidenav(open?: boolean) {
    this.isOpen = open ?? !this.isOpen;
    this.menuProvider.toggleSidebar(open ?? this.isOpen);
    document.documentElement.style.setProperty('--sidebar-width', this.isOpen ? '240px' : '64px');
  }

  async getUserEmail() {
    try {
      const email = await this.cognitoService.getUserEmail();
      this.userEmail = email;
    } catch (error) {
      console.error('Error retrieving user email:', error);
    }
  }

  async SsoSignIn() {
    try {
      await Auth.federatedSignIn({
        customProvider: 'azureadidp'
      });
    } catch (error) {
      console.log('Error signing in with Azure AD:', error);
    }
  }

  openFeedbackModal() {
    this.dialog.open(FeedbackModalComponent, {
      width: '500px',
      maxWidth: '90vw',
      panelClass: 'feedback-modal'
    });
  }

  async logout() {
    try {
      await Auth.signOut();
      this.userEmail = '';
      this.isAuthenticated = false;
      this.isAdmin = false;
    } catch (error) {
      console.log('Error signing out:', error);
    }
  }
}
